import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { User, LogOut, Heart, Menu, X, Settings, Shield } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetcher } from "../lib/api";
import { AuthDialog } from "./AuthDialog";
import { UserAvatar } from "./UserAvatar";
import { NotificationBell } from "./NotificationBell";
import { SearchAutocomplete } from "./SearchAutocomplete";
import { displayName } from "../lib/user";
import { useAuth } from "../context/AuthContext";

const LINKS = [
  { to: "/matches", label: "Matches" },
  { to: "/competitions", label: "Competitions" },
  { to: "/teams", label: "Teams" },
  { to: "/news", label: "News" },
  { to: "/transfers", label: "Transfers" },
  { to: "/compare", label: "Compare" },
  { to: "/games", label: "Games" },
  { to: "/community", label: "Community" },
];

export const Navbar = () => {
  const nav = useNavigate();
  const loc = useLocation();
  const { user, signOut } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => fetcher("/me"), enabled: !!user, staleTime: 300e3 });

  useEffect(() => {
    const h = () => setAuthOpen(true);
    window.addEventListener("open-auth", h);
    return () => window.removeEventListener("open-auth", h);
  }, []);

  return (
    <header className="relative z-50 border-b border-slate-200 bg-white">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 lg:px-8">
        <Link to="/" data-testid="logo" className="flex items-center gap-2 shrink-0">
          <img src="/soccer-favicon.png" alt="Soccer Bros" className="h-7 w-7 object-contain" />
          <span className="font-display text-lg font-black uppercase tracking-tight text-ink">Soccer <span className="text-volt">Bros</span></span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {LINKS.map((l) => {
            const active = loc.pathname.startsWith(l.to);
            return (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`relative px-3 py-2 text-sm font-bold transition-colors duration-200 ${active ? "text-ink" : "text-slate-500 hover:text-ink"}`}>
                {l.label}
                {active && <span className="absolute inset-x-3 -bottom-[13px] h-0.5 bg-ink" />}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {user && <NotificationBell />}
          <div className="relative">
          {user ? (
            <div className="relative">
              <button data-testid="user-menu-btn" onClick={() => setUserMenu((v) => !v)}
                className="flex items-center gap-2 rounded-full border border-slate-200 py-1.5 pl-1.5 pr-3 transition-colors duration-200 hover:border-ink">
                <UserAvatar user={user} size={24} />
                <span className="hidden max-w-[140px] truncate text-sm font-semibold md:inline" data-testid="user-display-name">{displayName(user)}</span>
              </button>
              {userMenu && (
                <div className="absolute right-0 z-50 mt-2 w-48 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg" onMouseLeave={() => setUserMenu(false)}>
                  <button data-testid="menu-favorites" onClick={() => { nav("/favorites"); setUserMenu(false); }}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-semibold hover:bg-slate-50">
                    <Heart className="h-4 w-4" /> My Feed
                  </button>
                  <button data-testid="menu-settings" onClick={() => { nav("/settings"); setUserMenu(false); }}
                    className="flex w-full items-center gap-2 border-t border-slate-100 px-4 py-2.5 text-left text-sm font-semibold hover:bg-slate-50">
                    <Settings className="h-4 w-4" /> Settings
                  </button>
                  {me?.is_admin && (
                    <button data-testid="menu-admin" onClick={() => { nav("/admin"); setUserMenu(false); }}
                      className="flex w-full items-center gap-2 border-t border-slate-100 px-4 py-2.5 text-left text-sm font-semibold hover:bg-slate-50">
                      <Shield className="h-4 w-4" /> Admin
                    </button>
                  )}
                  <button data-testid="menu-signout" onClick={() => { signOut(); setUserMenu(false); }}
                    className="flex w-full items-center gap-2 border-t border-slate-100 px-4 py-2.5 text-left text-sm font-semibold text-live hover:bg-slate-50">
                    <LogOut className="h-4 w-4" /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button data-testid="signin-btn" onClick={() => setAuthOpen(true)}
              className="flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-sm font-bold text-white transition-colors duration-200 hover:bg-slate-800">
              <User className="h-4 w-4" /> Sign in
            </button>
          )}
          </div>
        </div>

        <button className="md:hidden" onClick={() => setMenuOpen((v) => !v)} data-testid="mobile-menu-btn">
          {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-slate-200 bg-white px-4 py-3 md:hidden">
          <SearchAutocomplete
            placeholder="Search…"
            onNavigate={() => setMenuOpen(false)}
            wrapperClassName="mb-2"
            iconClassName="left-3 h-4 w-4"
            inputClassName="w-full rounded-full border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm outline-none"
          />
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setMenuOpen(false)} className="block py-2 text-sm font-bold text-slate-700">{l.label}</Link>
          ))}
        </div>
      )}

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </header>
  );
};
